import React, { useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  Typography,
  Tag
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appsApi } from '../api/apps';
import { App, CreateAppRequest, UpdateAppRequest, DistributionType } from '../types';

const { Option } = Select;

const { Title } = Typography;

const Apps: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingApp, setEditingApp] = useState<App | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: apps, isLoading } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.getApps(),
  });

  const createMutation = useMutation({
    mutationFn: appsApi.createApp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      message.success('Tạo app thành công!');
      setIsModalVisible(false);
      form.resetFields();
    },
    onError: (error: any) => {
      console.error('Create app error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert('❌ Lỗi khi tạo app: ' + errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ appId, data }: { appId: number; data: UpdateAppRequest }) => 
      appsApi.updateApp(appId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      message.success('Cập nhật app thành công!');
      setIsModalVisible(false);
      setEditingApp(null);
      form.resetFields();
    },
    onError: (error: any) => {
      console.error('Update app error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert('❌ Lỗi khi cập nhật app: ' + errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: appsApi.deleteApp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      message.success('Xóa app thành công!');
    },
    onError: (error: any) => {
      console.error('Delete app error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert('❌ Lỗi khi xóa app: ' + errorMessage);
    },
  });

  const handleCreate = () => {
    setEditingApp(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (app: App) => {
    setEditingApp(app);
    form.setFieldsValue(app);
    setIsModalVisible(true);
  };

  const handleDelete = (appId: number) => {
    deleteMutation.mutate(appId);
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      if (editingApp) {
        updateMutation.mutate({
          appId: editingApp.id,
          data: {
            name: values.name,
            description: values.description,
          }
        });
      } else {
        createMutation.mutate({
          name: values.name,
          description: values.description,
          distribution_type: values.distribution_type,
        });
      }
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingApp(null);
    form.resetFields();
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Tên App',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Distribution Type',
      dataIndex: 'distribution_type',
      key: 'distribution_type',
      render: (type: DistributionType) => {
        return (
          <Tag color={type === 'Store' ? 'green' : 'blue'}>
            {type === 'Store' ? 'Store' : 'Binary'}
          </Tag>
        );
      },
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('vi-VN'),
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => new Date(date).toLocaleString('vi-VN'),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: App) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Sửa
          </Button>
          <Popconfirm
            title="Bạn có chắc muốn xóa app này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Có"
            cancelText="Không"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>Quản lý Apps</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Thêm App
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={apps || []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          total: apps?.length || 0,
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} của ${total} apps`,
        }}
      />

      <Modal
        title={editingApp ? "Sửa App" : "Thêm App mới"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Tên App"
            rules={[{ required: true, message: 'Vui lòng nhập tên app!' }]}
          >
            <Input placeholder="Nhập tên app" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Mô tả"
          >
            <Input.TextArea
              placeholder="Mô tả về app"
              rows={3}
            />
          </Form.Item>

          {!editingApp && (
            <Form.Item
              name="distribution_type"
              label="Distribution Type"
              rules={[{ required: true, message: 'Vui lòng chọn loại distribution!' }]}
            >
              <Select placeholder="Chọn loại distribution">
                <Option value="Binary">Binary (Upload file)</Option>
                <Option value="Store">Store (Từ App Store/Play Store)</Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default Apps;
